import {
  EAttachmentType,
  getAttachmentIcon,
  getAttachmentColorClass,
  type TAttachmentType,
} from '@kognitos/lattice';
import type { Commonv1Value } from '@/generated/hooks/grimoire/models';
import type { TResolvedFile } from '@/modules/outputs/types';
import type { TUploadedFile } from '@/shared/types';
import type { FileTypeInfo } from '../types';

/**
 * Maps file extension to attachment type for design system icon/color utilities
 *
 * @param extension - Lowercase file extension (without dot)
 * @returns Attachment type enum value
 */
function getAttachmentTypeFromExtension(extension: string): TAttachmentType {
  // PDF files
  if (extension === 'pdf') {
    return EAttachmentType.PDF;
  }

  // Excel/spreadsheet files
  if (['xls', 'xlsx', 'csv', 'ods', 'tsv'].includes(extension)) {
    return EAttachmentType.EXCEL;
  }

  // Image files
  if (
    [
      'png',
      'jpg',
      'jpeg',
      'gif',
      'svg',
      'webp',
      'bmp',
      'ico',
      'tiff',
      'tif',
    ].includes(extension)
  ) {
    return EAttachmentType.IMAGE;
  }

  // Text/document files
  if (
    ['doc', 'docx', 'txt', 'rtf', 'odt', 'md', 'markdown'].includes(extension)
  ) {
    return EAttachmentType.TEXT;
  }

  // Default to generic file type
  return EAttachmentType.FILE;
}

/**
 * Determines the appropriate icon and color for a file based on its extension
 *
 * Uses design system attachment utilities for consistent icon and color mapping.
 * Supports common file types including PDF, Excel, images, and text documents.
 *
 * @param filename - The filename to analyze
 * @returns Icon type and color class for the file type
 */
export const getFileTypeInfo = (filename: string): FileTypeInfo => {
  const extension = filename.split('.').pop()?.toLowerCase() || '';
  const attachmentType = getAttachmentTypeFromExtension(extension);

  return {
    icon: getAttachmentIcon(attachmentType),
    colorClass: getAttachmentColorClass(attachmentType),
  };
};

/**
 * Parses file value from string format to UploadedFile array
 *
 * Supports multiple formats:
 * - New JSON format: {"name": "file.pdf", "resourceName": "orgs/123/files/abc"}
 * - New JSON array format: [{"name": "file.pdf", "resourceName": "orgs/123/files/abc"}]
 * - Legacy comma-separated: "orgs/123/files/abc,orgs/123/files/def"
 * - Legacy single string: "orgs/123/files/abc"
 *
 * @param value - The serialized file value
 * @param multiple - Whether multiple files are expected
 * @returns Array of uploaded file metadata
 */
export const parseFileValue = (
  value: string,
  multiple: boolean,
): TUploadedFile[] => {
  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);

    if (multiple) {
      // Multiple files mode
      if (Array.isArray(parsed)) {
        return parsed.map(item => {
          // New format: { name, resourceName }
          if (typeof item === 'object' && item.name && item.resourceName) {
            return item;
          }
          // Old format: just resourceName string
          return {
            name: item.split('/').pop() || item,
            resourceName: item,
          };
        });
      }
    } else {
      // Single file mode
      // New format: { name, resourceName }
      if (parsed.name && parsed.resourceName) {
        return [parsed];
      }
    }
  } catch {
    // If JSON parse fails, handle legacy formats
    if (multiple && value.includes(',')) {
      // Comma-separated resource names (old format)
      const resourceNames = value.split(',').map(v => v.trim());
      return resourceNames.map(resourceName => ({
        name: resourceName.split('/').pop() || resourceName,
        resourceName,
      }));
    }

    // Single resource name string (old format)
    return [
      {
        name: value.split('/').pop() || value,
        resourceName: value,
      },
    ];
  }

  return [];
};

/**
 * Serializes file metadata to string format for storage
 *
 * Stores files in new JSON format with both name and resourceName:
 * - Single file: {"name": "file.pdf", "resourceName": "orgs/123/files/abc"}
 * - Multiple files: [{"name": "file.pdf", "resourceName": "orgs/123/files/abc"}]
 *
 * @param files - Array of uploaded file metadata
 * @param multiple - Whether multiple files are supported
 * @returns Serialized file value as JSON string
 */
export const serializeFileValue = (
  files: TUploadedFile[],
  multiple: boolean,
): string => {
  if (files.length === 0) {
    return '';
  }

  if (multiple) {
    return JSON.stringify(files);
  }

  return JSON.stringify(files[0]);
};

/**
 * Converts a `Commonv1Value` file entry to a `TResolvedFile` that the
 * preview content component can render.
 *
 * @param value - The Commonv1Value containing a file reference
 * @returns A resolved file object for the PreviewContent component, or null if no file data
 */
export const toResolvedFile = (value: Commonv1Value): TResolvedFile | null => {
  const remote = value.file?.remote;
  if (remote) {
    return { type: 'file-remote', fileUrl: remote };
  }
  const inline = value.file?.inline;
  if (inline) {
    return { type: 'file-inline', name: inline.name ?? '', content: '' };
  }
  return null;
};
